/**
 * @fileoverview FacilityLink — Vendor Network Zustand store
 *
 * Manages client-side state for trades, global vendor directory,
 * and the org's enrolled vendor network (My Vendors).
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { create } from "zustand";
import type {
	Trade,
	Vendor,
	OrgVendorWithDetails,
} from "@/services/facility-link-service";

interface FacilityLinkState {
	// Trades
	trades: Trade[];
	tradesLoaded: boolean;

	// Global directory
	globalVendors: Vendor[];
	globalLoading: boolean;
	globalSearch: string;
	globalTradeFilter: number | undefined;

	// My vendor network
	myVendors: OrgVendorWithDetails[];
	myLoading: boolean;
	mySearch: string;
	myTradeFilter: number | undefined;

	// Active tab
	activeTab: "my-vendors" | "directory";

	// Actions
	setTrades: (trades: Trade[]) => void;
	setGlobalVendors: (vendors: Vendor[]) => void;
	setGlobalLoading: (loading: boolean) => void;
	setGlobalSearch: (search: string) => void;
	setGlobalTradeFilter: (tradeId: number | undefined) => void;
	setMyVendors: (vendors: OrgVendorWithDetails[]) => void;
	setMyLoading: (loading: boolean) => void;
	setMySearch: (search: string) => void;
	setMyTradeFilter: (tradeId: number | undefined) => void;
	setActiveTab: (tab: "my-vendors" | "directory") => void;

	// Optimistic updates
	addToMyVendors: (vendor: OrgVendorWithDetails) => void;
	removeFromMyVendors: (orgVendorId: number) => void;
	updateInMyVendors: (orgVendorId: number, updates: Partial<OrgVendorWithDetails>) => void;
}

export const useFacilityLinkStore = create<FacilityLinkState>((set) => ({
	trades: [],
	tradesLoaded: false,

	globalVendors: [],
	globalLoading: false,
	globalSearch: "",
	globalTradeFilter: undefined,

	myVendors: [],
	myLoading: false,
	mySearch: "",
	myTradeFilter: undefined,

	activeTab: "my-vendors",

	setTrades: (trades) => set({ trades, tradesLoaded: true }),
	setGlobalVendors: (globalVendors) => set({ globalVendors }),
	setGlobalLoading: (globalLoading) => set({ globalLoading }),
	setGlobalSearch: (globalSearch) => set({ globalSearch }),
	setGlobalTradeFilter: (globalTradeFilter) => set({ globalTradeFilter }),
	setMyVendors: (myVendors) => set({ myVendors }),
	setMyLoading: (myLoading) => set({ myLoading }),
	setMySearch: (mySearch) => set({ mySearch }),
	setMyTradeFilter: (myTradeFilter) => set({ myTradeFilter }),
	setActiveTab: (activeTab) => set({ activeTab }),

	addToMyVendors: (vendor) =>
		set((s) => ({ myVendors: [vendor, ...s.myVendors] })),
	removeFromMyVendors: (orgVendorId) =>
		set((s) => ({
			myVendors: s.myVendors.filter((v) => v.id !== orgVendorId),
		})),
	updateInMyVendors: (orgVendorId, updates) =>
		set((s) => ({
			myVendors: s.myVendors.map((v) =>
				v.id === orgVendorId ? { ...v, ...updates } : v,
			),
		})),
}));
